import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";

export default function DeleteStudentEnrollmentsBulkDialog({
  open,
  onOpenChange,
  courseId,
  selectedStudents = [],
  onDeleted,
}) {
  const [loading, setLoading] = useState(false);
  const { getToken } = useAuth();

  const normalizedSelections = useMemo(() => {
    const rows = Array.isArray(selectedStudents) ? selectedStudents : [];

    const items = rows
      .map((student) => {
        const userId =
          student?.user_id ?? student?.userId ?? student?.id ?? student?._id ?? null;
        const nameRaw = `${student?.first_name ?? ""} ${student?.last_name ?? ""}`.trim();
        const studentName = nameRaw || student?.name || student?.email || "Unknown";
        return { userId, studentName };
      })
      .filter((v) => v.userId != null && String(v.userId).trim() !== "");

    const uniqueByUserId = new Map();
    for (const item of items) {
      uniqueByUserId.set(String(item.userId), item);
    }

    return Array.from(uniqueByUserId.values());
  }, [selectedStudents]);

  const selectedCount = normalizedSelections.length;
  const canSubmit = Boolean(courseId) && selectedCount >= 2 && !loading;

  const handleBulkDelete = async () => {
    if (!courseId) {
      toast.error("Missing course id.");
      return;
    }

    if (selectedCount < 2) {
      toast.error("Select at least 2 enrollments to delete.");
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        toast.error("Not authenticated.");
        return;
      }

      const enrollments = normalizedSelections.map((item) => ({
        course_id: Number(courseId),
        user_id: item.userId,
      }));

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: {
          enrollments,
        },
      };

      const res = await axios.delete(
        api_endpoint + `/course/enrollment/bulk-delete`,
        reqConfig
      );

      const deletedCount =
        Number(res?.data?.deleted_count ?? res?.data?.deletedCount ?? selectedCount) ||
        selectedCount;

      toast.success(`Deleted ${deletedCount} enrollment${deletedCount === 1 ? "" : "s"}.`);
      onOpenChange(false);
      onDeleted?.({ deletedCount, userIds: normalizedSelections.map((s) => s.userId) });
    } catch (error) {
      console.error("Error bulk deleting student enrollments:", error);
      toast.error("Failed to delete selected enrollments.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md h-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Delete Student Enrollments</DialogTitle>
        </DialogHeader>

        <div className="font-body">
          Are you sure you want to delete <b>{selectedCount}</b> student enrollment
          {selectedCount === 1 ? "" : "s"} from this course? This action cannot be undone.
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button variant="" onClick={handleBulkDelete} disabled={!canSubmit}>
            {loading ? (
              <span className="flex items-center justify-center w-full">
                <ClipLoader size={20} color="#fff" />
              </span>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
